import Image from "next/image";
import Reveal from "@/components/ui/Reveal";

type TeamMember = {
  name: string;
  title: string;
  education: string;
  linkedin: string;
  /** Path under /public to a headshot; cropped to a circle, weighted towards the top. */
  photo: string;
};

const team: readonly TeamMember[] = [
  {
    name: "Dr. Naveed Iftikhar",
    title: "Founder & CEO",
    education: "PhD, Entrepreneurship/Entrepreneurial Studies, University of Delaware",
    linkedin: "https://www.linkedin.com/in/navift/",
    photo: "/images/trainers/naveed.png",
  },
  {
    name: "Dr. Muhammad Umair",
    title: "CTO",
    education: "PhD in Multimodal AI, University of Electronic Science and Technology of China",
    linkedin: "https://www.linkedin.com/in/muhammad-umair-phd-ab4ba2107/",
    photo: "/images/trainers/umair.png",
  },
  {
    name: "Alizey Zaidi",
    title: "Director, U.S. Enterprise Partnerships & Growth",
    education: "Ed.M., Harvard University",
    linkedin: "https://www.linkedin.com/in/alizey-zaidi",
    photo: "/images/trainers/alizey.jpeg",
  },
];

/**
 * Leadership (#leadership): a centred heading over a card per person: a large portrait, then
 * name, title, education and LinkedIn's logo as the link, all centred in the column; each text
 * row lines up across the cards.
 */
export default function OurTeam() {
  return (
    <section
      id="leadership"
      aria-labelledby="leadership-heading"
      className="scroll-mt-20 bg-white px-margin-mobile md:px-margin-desktop py-size-xl"
    >
      <div className="max-w-[1440px] mx-auto">
        {/* No max width on the heading: it fits on one line from tablets up, and wraps (balanced)
            on phones instead of overflowing. Don't add whitespace-nowrap here. */}
        <Reveal className="text-center mb-size-lg">
          <span className="font-label-md text-label-md text-mkt-primary uppercase tracking-widest">
            Leadership
          </span>
          <h2
            id="leadership-heading"
            className="mt-size-sm font-display-lg text-[clamp(24px,3vw,44px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black text-balance break-words"
          >
            The humans building the future of learning.
          </h2>
        </Reveal>

        {/* Each card is five rows (photo, name, title, education, LinkedIn) on a shared subgrid,
            so every row lines up across the cards however long a title or degree runs. Row gaps
            are zero here and spacing comes from the items, so the gaps between a card's own rows
            stay tight; on phones, where cards stack, the LinkedIn row adds the space below. */}
        <ul className="list-none pl-0 mx-auto max-w-[800px] grid grid-cols-1 sm:grid-cols-3 gap-x-gutter gap-y-0">
          {team.map(({ name, title, education, linkedin, photo }, index) => (
            <li key={name} className="row-span-5 grid grid-rows-subgrid">
              <Reveal
                delayMs={index === 0 ? undefined : index === 1 ? 100 : 200}
                className="row-span-5 grid grid-rows-subgrid"
              >
                <article className="row-span-5 grid grid-rows-subgrid">
                  {/* Circular and centred in its column. The headshots are upper-body shots, so
                      the crop sits above centre (object-[50%_20%]) to keep the face in the middle. */}
                  <div className="justify-self-center relative w-40 aspect-square overflow-hidden rounded-full bg-mkt-primary/10 ring-4 ring-white shadow-[0_8px_28px_-4px_rgba(0,60,185,0.25)]">
                    <Image
                      src={photo}
                      alt={name}
                      fill
                      sizes="160px"
                      className="object-cover object-[50%_20%]"
                    />
                  </div>
                  <h3 className="mt-size-sm text-center font-headline-md text-body-lg font-bold text-mkt-inarax-black">
                    {name}
                  </h3>
                  <p className="mt-1 text-center font-body-md text-sm font-bold text-mkt-primary">{title}</p>
                  <p className="mt-1 text-center font-body-md text-sm text-mkt-on-surface-variant">{education}</p>
                  <div className="mt-size-sm pb-size-lg sm:pb-0 text-center">
                    <a
                      href={linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${name} on LinkedIn`}
                      className="inline-flex rounded transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mkt-primary-container"
                    >
                      {/* LinkedIn's official logo (public/images/linkedin-logo.svg), at its own colours. */}
                      <Image
                        src="/images/linkedin-logo.png"
                        alt=""
                        width={84}
                        height={21}
                        unoptimized
                        className="h-5 w-auto"
                      />
                    </a>
                  </div>
                </article>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
